import React, {useState, useRef, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Text,
    Flex,
    Box,
    Image,
    Button,
    IconButton,
    Skeleton,
    useStyleConfig,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    ListItem,
    UnorderedList
} from '@chakra-ui/react'
import {Ratings} from '../../components/ratings'
import {
    InfoIconFilled,
    ThumbDownActiveIcon,
    ThumbDownIcon,
    ThumbUpActiveIcon,
    ThumbUpIcon,
    VerifiedIcon,
    ZoomIcon,
    ZoomOutIcon
} from '../../components/icons'
import Slider from '../../components/slider'
import {FormattedMessage, useIntl} from 'react-intl'
import {getDateDifference} from '../../utils/utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useContentFeedback from '../../bazaarvoice-api/hooks/use-content-feedback'
import {useTranslatableReview} from '../../hooks/use-translatable-review'
import {trackRRPhotoEnlarge, trackRRClickTranslation} from '../../tracking/analytics'

const Review = ({review}) => {
    const styles = useStyleConfig('Review')
    const textContainer = useRef()
    const commentContainer = useRef()
    const [expanded, setExpanded] = useState(false)
    const [commentExpanded, setCommentExpanded] = useState(false)
    const [isEllipsisActive, setIsEllipsisActive] = useState(false)
    const [isCommentEllipsisActive, setIsCommentEllipsisActive] = useState(false)
    const [itemsPerSlide, setItemsPerSlide] = useState(3)
    const ZoomIconType = itemsPerSlide === 1.5 ? ZoomOutIcon : ZoomIcon
    const contentFeedback = useContentFeedback({contentType: 'Review', contentId: review.Id})
    const {
        isTranslationEnabled,
        setTranslate,
        isShowTranslated,
        reviewLang,
        reviewTranslation,
        commentTranslation
    } = useTranslatableReview(review)
    const rootRef = useRef()
    const {formatDate, formatTime, formatMessage} = useIntl()

    const {differenceDays} = useMemo(
        () => getDateDifference(review.SubmissionTime),
        [review.SubmissionTime]
    )

    useEffect(() => {
        setIsEllipsisActive(
            textContainer.current?.offsetHeight < textContainer.current?.scrollHeight
        )
        setIsCommentEllipsisActive(
            commentContainer.current?.offsetHeight < commentContainer.current?.scrollHeight
        )
    }, [review])

    const reviewResponseDate = useMemo(
        () =>
            review.ClientResponses.length > 0 &&
            formatMessage(
                {id: 'global.dateAtTime', defaultMessage: '{date} at {time}'},
                {
                    date: formatDate(new Date(review.ClientResponses[0].Date), {
                        year: 'numeric',
                        day: 'numeric',
                        month: 'long'
                    }),
                    time: formatTime(new Date(review.ClientResponses[0].Date), {
                        hour: 'numeric',
                        minute: 'numeric'
                    })
                }
            ),
        [formatMessage, formatDate, formatTime, review]
    )

    if (!review) {
        return null
    }

    return (
        <Flex {...styles.container} ref={rootRef}>
            <Flex {...styles.textContainer}>
                <Ratings rating={review.Rating} {...styles.rating} />

                <Skeleton isLoaded={!reviewTranslation.loading}>
                    <Text data-testid="review-heading" as="h3" textStyle="Regular XLarge">
                        {review.Title}
                    </Text>
                </Skeleton>
                <Skeleton isLoaded={!reviewTranslation.loading}>
                    <Text
                        ref={textContainer}
                        {...styles.reviewText}
                        noOfLines={expanded ? null : '5'}
                        lang={reviewTranslation.displayLang}
                    >
                        {reviewTranslation.displayText}
                    </Text>
                </Skeleton>

                {isEllipsisActive && (
                    <Button
                        variant="link"
                        onClick={() => {
                            setExpanded(!expanded)
                        }}
                    >
                        {expanded ? (
                            <FormattedMessage id="global.show_less" defaultMessage="Show less" />
                        ) : (
                            <FormattedMessage id="global.show_more" defaultMessage="Show more" />
                        )}
                    </Button>
                )}

                {isTranslationEnabled && (
                    <Flex direction="column" gap={2} width="full">
                        <Flex
                            width="full"
                            gap={{md: 8}}
                            justify={{base: 'space-between', md: 'start'}}
                            align="center"
                        >
                            {isShowTranslated || reviewTranslation.loading ? (
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        setTranslate(false)
                                    }}
                                >
                                    <FormattedMessage
                                        id="global.see_original_text"
                                        defaultMessage="See original text"
                                    />
                                </Button>
                            ) : (
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        setTranslate(true)
                                        trackRRClickTranslation(reviewLang)
                                    }}
                                >
                                    <FormattedMessage
                                        id="global.translate"
                                        defaultMessage="Translate"
                                    />
                                </Button>
                            )}

                            <a
                                data-testid="google-translate-attribution"
                                href="http://translate.google.com"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Image
                                    src={getAssetUrl('static/img/global/google-translate.png')}
                                    width="179px"
                                    height="19px"
                                />
                            </a>
                        </Flex>

                        {reviewTranslation.error && (
                            <Text textStyle="Regular Small" color="stokkeFunctional.alertRed">
                                <FormattedMessage
                                    id="global.error"
                                    defaultMessage="An error occurred. Try again."
                                />
                            </Text>
                        )}
                    </Flex>
                )}

                <Flex {...styles.buyerInfoContainer}>
                    <Box>
                        <Text>
                            <FormattedMessage
                                defaultMessage="Submitted {daysCount} day(s) ago"
                                values={{daysCount: differenceDays}}
                                id="reviews.submitted_days_ago"
                            />
                        </Text>
                        <Text>
                            <FormattedMessage
                                defaultMessage="By {userNickname}"
                                values={{userNickname: review.UserNickname}}
                                id="reviews.user_nickname"
                            />
                        </Text>
                        <Text>
                            <FormattedMessage
                                defaultMessage="From {userLocation}"
                                values={{userLocation: review.UserLocation}}
                                id="reviews.user_location"
                            />
                        </Text>
                    </Box>
                    <Flex gap={2} alignItems="center" textStyle="Semi Bold Small">
                        <VerifiedIcon boxSize={4} />
                        <FormattedMessage
                            id="reviews.verified_buyer"
                            defaultMessage="Verified Buyer"
                        />
                    </Flex>
                </Flex>

                {review.Photos?.length > 0 && (
                    <Flex alignItems="center" gap={2} position="relative">
                        <Slider
                            itemsPerSlide={itemsPerSlide}
                            variant="reviews"
                            label={formatMessage({
                                defaultMessage: 'Reviews carousel',
                                id: 'reviews.slider.assistive_msg.label'
                            })}
                        >
                            {review.Photos.map((photo, index) => (
                                <Image
                                    key={index}
                                    src={photo.Sizes.normal.Url}
                                    data-testid="review-image"
                                    aria-label={formatMessage(
                                        {id: 'reviews.image', defaultMessage: 'Image {imgIndex}'},
                                        {imgIndex: index + 1}
                                    )}
                                />
                            ))}
                        </Slider>
                        <IconButton
                            variant="zoom"
                            icon={<ZoomIconType color="stokkeCore.white" />}
                            {...(itemsPerSlide === 1.5 && {
                                position: 'absolute',
                                left: 4,
                                top: 4,
                                zIndex: 1
                            })}
                            onClick={() => {
                                if (itemsPerSlide === 3) {
                                    trackRRPhotoEnlarge(review.Photos[0].Sizes.normal.Url)
                                    setItemsPerSlide(1.5)
                                    return
                                }
                                setItemsPerSlide(3)
                            }}
                        />
                    </Flex>
                )}

                <Flex {...styles.feedbackContainer}>
                    <Flex {...styles.feedbackHelpfulnessContainer}>
                        <Text as="h3" {...styles.feedbackHelpfulnessText}>
                            <FormattedMessage
                                defaultMessage="Was this review helpful to you?"
                                id="reviews.was_this_review_helpful"
                            />
                        </Text>
                        <Flex {...styles.feedbackHelpfulnessButtonsContainer}>
                            <Button
                                variant="helpfulness"
                                isActive={contentFeedback.helpful.vote === 'Positive'}
                                isDisabled={
                                    contentFeedback.helpful.submitting ||
                                    contentFeedback.helpful.submitted
                                }
                                isLoading={contentFeedback.helpful.submitting}
                                leftIcon={
                                    contentFeedback.helpful.vote === 'Positive' ? (
                                        <ThumbUpActiveIcon width="22px" height="21px" />
                                    ) : (
                                        <ThumbUpIcon width="22px" height="21px" />
                                    )
                                }
                                onClick={() => {
                                    contentFeedback.helpful.submit('Positive')
                                }}
                                aria-label={formatMessage({
                                    id: 'reviews.was_this_review_helpful.vote_positive.assistive_msg',
                                    defaultMessage: 'Review is helpful'
                                })}
                                aria-current={
                                    contentFeedback.helpful.submitted
                                        ? contentFeedback.helpful.vote === 'Positive'
                                        : null
                                }
                            >
                                {review.TotalPositiveFeedbackCount +
                                    (contentFeedback.helpful.vote === 'Positive' ? 1 : 0)}
                            </Button>
                            <Button
                                variant="helpfulness"
                                isActive={contentFeedback.helpful.vote === 'Negative'}
                                isDisabled={
                                    contentFeedback.helpful.submitting ||
                                    contentFeedback.helpful.submitted
                                }
                                isLoading={contentFeedback.helpful.submitting}
                                leftIcon={
                                    contentFeedback.helpful.vote === 'Negative' ? (
                                        <ThumbDownActiveIcon />
                                    ) : (
                                        <ThumbDownIcon />
                                    )
                                }
                                onClick={() => {
                                    contentFeedback.helpful.submit('Negative')
                                }}
                                aria-label={formatMessage({
                                    id: 'reviews.was_this_review_helpful.vote_negative.assistive_msg',
                                    defaultMessage: 'Review is not helpful'
                                })}
                                aria-current={
                                    contentFeedback.helpful.submitted
                                        ? contentFeedback.helpful.vote === 'Negative'
                                        : null
                                }
                            >
                                {review.TotalNegativeFeedbackCount +
                                    (contentFeedback.helpful.vote === 'Negative' ? 1 : 0)}
                            </Button>
                        </Flex>
                    </Flex>
                    <Flex {...styles.feedbackInappropriateContainer}>
                        {contentFeedback.inappropriate.submitted ? (
                            <Text {...styles.feedbackInappropriateText}>
                                <FormattedMessage
                                    defaultMessage="The review has been reported. Thank you!"
                                    id="reviews.feedback.inappropriate.submitted"
                                />
                            </Text>
                        ) : (
                            <>
                                <Button
                                    variant="link"
                                    isDisabled={
                                        contentFeedback.inappropriate.submitting ||
                                        contentFeedback.inappropriate.submitted
                                    }
                                    isLoading={contentFeedback.inappropriate.submitting}
                                    onClick={contentFeedback.inappropriate.submit}
                                >
                                    <FormattedMessage
                                        defaultMessage="Report as inappropriate"
                                        id="reviews.feedback.inappropriate.submit"
                                    />
                                </Button>
                                <Popover
                                    placement="top"
                                    boundary={rootRef.current}
                                    variant="feedbackReportTooltip"
                                    closeOnBlur={false}
                                >
                                    <PopoverTrigger>
                                        <IconButton
                                            variant="unstyled"
                                            width={3}
                                            minWidth={3}
                                            height={3}
                                            icon={
                                                <InfoIconFilled boxSize={3} verticalAlign="top" />
                                            }
                                        />
                                    </PopoverTrigger>
                                    <PopoverContent>
                                        <PopoverCloseButton />
                                        <PopoverHeader as="h3">
                                            <Text>
                                                <FormattedMessage
                                                    defaultMessage="Report this review."
                                                    id="reviews.feedback.inappropriate.tooltip.header"
                                                />
                                            </Text>
                                        </PopoverHeader>
                                        <PopoverArrow />
                                        <PopoverBody>
                                            <Text>
                                                <FormattedMessage
                                                    defaultMessage="Here are some common reasons customers report reviews:"
                                                    id="reviews.feedback.inappropriate.tooltip.body"
                                                />
                                            </Text>
                                            <UnorderedList>
                                                <ListItem>
                                                    <FormattedMessage
                                                        defaultMessage="Harassment, profanity"
                                                        id="reviews.feedback.inappropriate.tooltip.bp1"
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <FormattedMessage
                                                        defaultMessage="Hate Speech"
                                                        id="reviews.feedback.inappropriate.tooltip.bp2"
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <FormattedMessage
                                                        defaultMessage="Spam, advertisement, promotions"
                                                        id="reviews.feedback.inappropriate.tooltip.bp3"
                                                    />
                                                </ListItem>
                                            </UnorderedList>
                                        </PopoverBody>
                                    </PopoverContent>
                                </Popover>
                            </>
                        )}
                    </Flex>
                </Flex>
            </Flex>

            {review.ClientResponses.length > 0 && (
                <Flex
                    flexDirection="column"
                    paddingX={4}
                    paddingY={6}
                    gap={6}
                    backgroundColor="stokkeBeige.mid"
                >
                    <Flex
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-between"
                        rowGap={2}
                        columnGap={6}
                    >
                        <Text flexShrink={0} textStyle="Regular XLarge">
                            <FormattedMessage
                                defaultMessage="Response from Customer Care"
                                id="review.comment.heading"
                            />
                        </Text>
                        <Text
                            flexShrink={0}
                            textStyle="Regular Small"
                            color="stokkeGray.accessibleText"
                        >
                            {reviewResponseDate}
                        </Text>
                    </Flex>

                    <Skeleton isLoaded={!commentTranslation.loading}>
                        <Text
                            ref={commentContainer}
                            {...styles.reviewText}
                            noOfLines={commentExpanded ? null : '5'}
                            lang={commentTranslation.displayLang}
                        >
                            {commentTranslation.displayText}
                        </Text>
                    </Skeleton>

                    {isCommentEllipsisActive && (
                        <Button
                            variant="link"
                            onClick={() => {
                                setCommentExpanded((e) => !e)
                            }}
                        >
                            {commentExpanded ? (
                                <FormattedMessage
                                    id="global.show_less"
                                    defaultMessage="Show less"
                                />
                            ) : (
                                <FormattedMessage
                                    id="global.show_more"
                                    defaultMessage="Show more"
                                />
                            )}
                        </Button>
                    )}

                    {commentTranslation.error && (
                        <Text textStyle="Regular Small" color="stokkeFunctional.alertRed">
                            <FormattedMessage
                                id="global.error"
                                defaultMessage="An error occurred. Try again."
                            />
                        </Text>
                    )}
                </Flex>
            )}
        </Flex>
    )
}

Review.propTypes = {
    review: PropTypes.object,
    variant: PropTypes.string
}

export default Review
